// Libraries
import React, { FC, UIEvent, useEffect, useMemo, useRef, useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"

// Utils
import { throttle, getImgixImageData } from "@utils"
import { logCustomGAEvent } from "../../../../_utils"
import { GA_CUSTOM_EVENTS } from "../../../../_store/constants"

// Shared Components
import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

// Store
import {
  addQuestionToInterview,
  permanentlyDeleteQuestion,
  removeQuestionFromInterview,
  revertQuestionEditedText,
  startDraggingQuestion,
  toggleEditingQuestion,
  updateQuestionEditedText,
} from "../../../../_store/actions"
import {
  QUESTION_LIST_VARIANTS,
} from "../../../../_store/constants"
import { DispatchType, Question, StateType } from "../../../../_store/types"

// Sub-components
import DropZone from "../DropZone/DropZone"
import JobTitleField from "../JobTitleField/JobTitleField"
import QuestionCard from "../QuestionCard/QuestionCard"

// Styled Components
import {
  QuestionListActionsDIV,
  QuestionListFilterDrawerDIV,
  QuestionListHEADER,
  QuestionListLibraryDragHelperDIV,
  QuestionListSECTION,
  QuestionListScrollWrapperDIV,
  QuestionListScrollerDIV,
} from "./QuestionList.styles"
import QuestionListActionButtons from "../QuestionsListActionButtons/QuestionsListActionButtons"

type QuestionListProps = {
  dispatch: DispatchType
  isCollapsed: boolean
  isMobile: boolean
  onAddNewQuestion: () => void
  onClickExport: () => void
  onResetState: () => void
  onToggleCollapsed: () => void
  state: StateType
  variant: QUESTION_LIST_VARIANTS
}

const HAS_SHOWN_EDIT_PROMPT_LS_KEY = "hs--interviewBuilder--hasShownEditPrompt"
export const showEditPromptAnimationLengthMs = 5000

const QuestionList: FC<QuestionListProps> = ({
  dispatch,
  isCollapsed,
  isMobile,
  onAddNewQuestion,
  onClickExport,
  onResetState,
  onToggleCollapsed,
  state,
  variant,
}) => {
  const isLibrary = variant === QUESTION_LIST_VARIANTS.LIBRARY
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false)
  const [isScrolled, setIsScrolled] = useState<boolean>(false)
  const [currentCategory, setCurrentCategory] = useState<
    Question["category"] | null
  >(null)
  const [showEditPrompt, setShowEditPrompt] = useState<boolean>(false)
  const [windowHeight, setWindowHeight] = useState<number | null>(null)
  const [invisibleDropZoneHeight, setInvisibleDropZoneHeight] = useState<number>(0)
  const ScrollerRef = useRef<HTMLDivElement | null>(null)
  const hasActiveDropZone = useMemo(() => typeof state.isDraggingData.dropZoneIndex === "number", [state.isDraggingData.dropZoneIndex])
  const isDraggingQuestion = !!state.isDraggingData.questionId

  // Determine the window height on mobile devices
  useEffect(() => {
    const calculateWindowHeight = throttle(() => {
      setWindowHeight(window.innerHeight)
    }, 1000)

    window.addEventListener("resize", calculateWindowHeight)

    calculateWindowHeight()

    return () => {
      window.removeEventListener("resize", calculateWindowHeight)
    }
  }, [])

  // Determine the InvisibleDropZone height in Interview on desktop devices
  useEffect(() => {
    if (isLibrary || isMobile || !ScrollerRef.current) return

    if (ScrollerRef.current.clientHeight <= ScrollerRef.current.scrollHeight) {
      const lastCardEl = [...ScrollerRef.current.querySelectorAll(".QuestionCard")].pop()
      if (!lastCardEl) return
      // Bottom of the container - bottom of the last QuestionCard - bottom margin of QuestionCard = Height of the InvisibleDropZone
      setInvisibleDropZoneHeight(ScrollerRef.current.getBoundingClientRect().bottom - lastCardEl?.getBoundingClientRect().bottom - 10)
      return
    }
    // If the scrollHeight is greater than the clientHeight then we don't need the InvisibleDropZone
    setInvisibleDropZoneHeight(0)
  }, [state.interview.questions.length, isLibrary, isMobile])

  // Determine whether to show the "How to edit" prompt/tooltip
  useEffect(() => {
    const hasShownEditPrompt = !!window.localStorage.getItem(HAS_SHOWN_EDIT_PROMPT_LS_KEY)
    if (isLibrary || hasShownEditPrompt || isCollapsed) return

    const isExpandingOnMobile = isMobile && !isCollapsed
    const collapseAnimationLengthMs = 400

    if (isExpandingOnMobile) {
      setTimeout(() => {
        setShowEditPrompt(true)
      }, collapseAnimationLengthMs)
    } else {
      setShowEditPrompt(true)
    }

    setTimeout(() => {
      setShowEditPrompt(false)
      window.localStorage.setItem(HAS_SHOWN_EDIT_PROMPT_LS_KEY, "true")
    },
      isExpandingOnMobile ?
        showEditPromptAnimationLengthMs + collapseAnimationLengthMs :
        showEditPromptAnimationLengthMs
    )
  }, [isCollapsed, isMobile])

  // Used in the QuestionCard .map() loop on desktop to generate category headings
  let currentCategoryInIteration: string | undefined

  const questionsToDisplay = useMemo(
    () => {
      const _questionsToDisplay = isLibrary
        ? // Library questions to display
        isMobile
          ? // On mobile, just filter by the currentCategory
          Object.keys(state.questions)
            .filter(
              (qid) =>
                !(
                  currentCategory &&
                  state.questions[qid].category !== currentCategory
                )
            )
            .map((qid) => state.questions[qid])
          : // On desktop, filter out cards that are either not in the currentCategory or are in the Interview already, then sort by category
          Object.keys(state.questions)
            .filter(
              (qid) =>
                !(
                  currentCategory &&
                  state.questions[qid].category !== currentCategory
                ) && !state.interview.questions.includes(qid)
            )
            .sort((qidA: Question["id"], qidB: Question["id"]) => {
              if (!currentCategory) {
                const categoryA = state.questions[qidA]?.category || "ZZZZ" // lol fallback
                const categoryB = state.questions[qidB]?.category || "ZZZZ" // lol fallback

                if (categoryA > categoryB) return 1
                if (categoryA < categoryB) return -1
              }
              return 0
            })
            .map((qid) => state.questions[qid])
        : // Interview questions to display = any with ids present in state.interview.questions
        state.interview.questions.map((qid) => state.questions[qid])

      // Reset the filter if there are no questions to display
      if (currentCategory && _questionsToDisplay.length === 0) setCurrentCategory(null)

      return _questionsToDisplay
    },
    [isMobile, state.interview.questions, state.questions, currentCategory]
  )

  const categories = useMemo(
    () =>
      [
        ...new Set(
          Object.keys(state.questions)
            .filter((qid) => isMobile || !state.interview.questions.includes(qid))
            .map(
              (qid) => state.questions[qid].category
            )
        ),
      ].sort(),
    [isMobile, state.interview.questions, state.questions]
  )

  // Scroll handler to see if the column has been scrolled at all, so that we can add the tiny
  // little box-shadow to the header element to create a nice dilineation
  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    setIsScrolled((e.target as HTMLDivElement).scrollTop > 0)
  }

  // Throttled to save resources
  const throttledHandleScroll = useMemo(() => throttle(handleScroll, 400), [])

  const handleFinishedEditingCard = (question: Question, text: string): void => {
    dispatch(toggleEditingQuestion(question.id))
    // Only update textEdited if the text has deviated from textDefault, otherwise revert it (just to be safe)
    if (text === question.textDefault) {
      dispatch(revertQuestionEditedText(question.id))
      return
    }
    dispatch(updateQuestionEditedText(question.id, text))
  }

  const debouncedHandleFinishedEditingCard = useMemo(() => throttle(handleFinishedEditingCard, 100), [])

  const handleAddNewQuestion = () => {
    // Scroll to the top so that the new question is immediately visible
    if (ScrollerRef.current) ScrollerRef.current.scrollTo({ top: 0, behavior: "smooth" })
    onAddNewQuestion()
  }

  return (
    <QuestionListSECTION
      className={[
        `is-${variant}`,
        isCollapsed ? "is-collapsed" : "",
        isDraggingQuestion ? "is-dragging-question" : "",
        isFilterDrawerOpen ? "is-filter-drawer-open" : "",
        isScrolled ? "is-scrolled" : "",
        currentCategory ? "is-filtered" : "",
      ].filter(v => v).join(" ")}
      style={{
        "--window-height": windowHeight ? `${windowHeight}px` : undefined
      } as React.CSSProperties}
    >
      <QuestionListHEADER>
        <h3 className={isMobile ? "font-size--L" : "font-size--L"}>
          {isLibrary ? "Question Library" : "Your Interview"}
        </h3>
        <QuestionListActionsDIV>
          {isLibrary && (
            <button
              className="filter-toggle-button"
              onClick={() => {
                setIsFilterDrawerOpen((_isFilterDrawerOpen) => {
                  // If the filter drawer is opened while the column is collapsed, open it!
                  if (!_isFilterDrawerOpen && isCollapsed) onToggleCollapsed()
                  return !_isFilterDrawerOpen
                })
              }}
            >
              <Icon
                size="24px"
                type={isFilterDrawerOpen ? ICON_TYPES["close"] : ICON_TYPES["filter"]}
              />
            </button>
          )}
          {isMobile && (
            <button
              className="collapse-button"
              onClick={() => {
                if (!isCollapsed) setIsFilterDrawerOpen(false)
                onToggleCollapsed()
              }}
            >
              <Icon
                type={ICON_TYPES["chevron-left"]}
                size="16px"
              />
            </button>
          )}
          {!isMobile && !isLibrary && (
            <QuestionListActionButtons
              onAddNewQuestion={onAddNewQuestion}
              onResetState={onResetState}
              onClickExport={onClickExport}
            />
          )}
        </QuestionListActionsDIV>
        {isLibrary && (
          <QuestionListFilterDrawerDIV>
            <p>Category</p>
            <div>
              {categories.filter(category => category?.length && category?.length > 1).map((category) => (
                <button
                  className={currentCategory === category ? "is-current" : ""}
                  key={category}
                  onClick={() => {
                    setCurrentCategory((_currentCategory) =>
                      _currentCategory === category ? null : category
                    )
                    logCustomGAEvent(GA_CUSTOM_EVENTS.FILTER_QUESTION_LIBRARY, {
                      category: category as string,
                    })
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </QuestionListFilterDrawerDIV>
        )}
      </QuestionListHEADER>
      {/* If the dragged question's origin is INTERVIEW and this is the LIBRARY column, show the removal helper text */}
      {!isMobile &&
        isLibrary &&
        isDraggingQuestion &&
        state.isDraggingData.questionOrigin !==
        QUESTION_LIST_VARIANTS.LIBRARY && (
          <QuestionListLibraryDragHelperDIV id="remove-drop-zone">
            <div>
              <p>Drop the question here to return it to the Library.</p>
            </div>
          </QuestionListLibraryDragHelperDIV>
        )}
      {/* If the dragged question's origin is LIBRARY and this is the INTERVIEW column and there are no questions in the INTERVIEW, show the add helper text */}
      {!isMobile &&
        !isLibrary &&
        state.interview.questions.length === 0 && (
          <QuestionListLibraryDragHelperDIV
            id="add-drop-zone"
            className={
              isDraggingQuestion && state.isDraggingData.questionOrigin === QUESTION_LIST_VARIANTS.LIBRARY ?
                "is-dragging" :
                ""
            }
          >
            <div>
              <GatsbyImage
                alt="Illustration of questions in a list"
                image={getImgixImageData(
                  "/images/pages/interview-builder/InterviewBuilder--DragHelper--Interview-illo.png",
                  120,
                  1.16
                )}
              />
              <p>
                Drag questions from the left column and drop them here to build your interview.
                <br /><br />
                Sort, edit, remove or add questions at any time.
              </p>
            </div>
          </QuestionListLibraryDragHelperDIV>
        )}
      <QuestionListScrollWrapperDIV
        className={`${isCollapsed ? "is-collapsed" : ""} ${isLibrary ? "is-library" : "is-interview"}`}
      >
        <QuestionListScrollerDIV
          onScroll={isMobile ? undefined : throttledHandleScroll}
          ref={ScrollerRef}
        >
          {!isLibrary && (
            <JobTitleField
              dispatch={dispatch}
              value={state.interview.jobTitle}
            />
          )}
          {questionsToDisplay.map((question, i) => {
            const shouldRenderCategoryHeading =
              !isMobile &&
              isLibrary &&
              currentCategoryInIteration !== question.category

            if (shouldRenderCategoryHeading) {
              currentCategoryInIteration = question.category
            }

            const shouldShowEditPrompt =
              showEditPrompt &&
              (
                questionsToDisplay.length >= 2 && i === 1 ||
                questionsToDisplay.length === 1 && i === 0
              )

            return (
              <React.Fragment key={question.id}>
                {/* If this is the Library on Desktop, render a category heading when needed */}
                {shouldRenderCategoryHeading && (
                  <p className="category-heading">
                    <span>{question.category}</span><span className="category-line" />
                  </p>
                )}
                {/* If the drop zone index is at this index, render it */}
                {
                  !isLibrary &&
                  hasActiveDropZone &&
                  state.isDraggingData.dropZoneIndex === i && (
                    <DropZone index={i} />
                  )
                }
                <QuestionCard
                  index={i}
                  isCollapsed={isCollapsed}
                  isInInterview={state.interview.questions.includes(question.id)}
                  isEditing={state.isEditingQuestionId === question.id}
                  isMobile={isMobile}
                  isPlaceholder={state.isDraggingData.questionId === question.id}
                  onClickAdd={(): void => dispatch(addQuestionToInterview(question.id, 0))}
                  onClickEdit={(): void => dispatch(toggleEditingQuestion(question.id))}
                  onClickPermanentlyDelete={(): void => dispatch(permanentlyDeleteQuestion(question.id))}
                  onClickRemove={(): void => dispatch(removeQuestionFromInterview(question.id))}
                  onClickReset={(): void => dispatch(revertQuestionEditedText(question.id))}
                  onFinishedEditing={(text: string): void => debouncedHandleFinishedEditingCard(question, text)}
                  onStartDragging={(clientRect: DOMRect): void =>
                    dispatch(
                      startDraggingQuestion(question.id, i, variant, clientRect)
                    )
                  }
                  showEditPrompt={shouldShowEditPrompt}
                  variant={variant}
                  {...question}
                />
                {/* If this is the last Question but the drop zone index is after, display it here. */}
                {
                  !isLibrary &&
                  hasActiveDropZone &&
                  i + 1 === questionsToDisplay.length &&
                  state.isDraggingData.dropZoneIndex === i + 1 && (
                    <DropZone index={i + 1} />
                  )
                }
              </React.Fragment>
            )
          })}
          {!isLibrary && (
            <div
              className="InvisibleDropZone"
              data-last-index={questionsToDisplay.length - 1}
              style={{ height: invisibleDropZoneHeight }}
            />
          )}
        </QuestionListScrollerDIV>
      </QuestionListScrollWrapperDIV>
      {isMobile && !isLibrary && (
        <QuestionListActionButtons
          onAddNewQuestion={handleAddNewQuestion}
          onResetState={onResetState}
          onClickExport={onClickExport}
        />
      )}
    </QuestionListSECTION>
  )
}

export default QuestionList
